import { useState } from "react"
import { ArrowLeft, Loader2, Mail, ShieldCheck } from "lucide-react"

import { Button } from "@/components/ui/button"
import { cn } from "@/lib/utils"
import { AppRoutes } from "@/shared/lib/routes"
import { LegalLink } from "@/shared/components/legal-link"
import { FINAL_VIEW } from "./cosmic-map"
import { CosmicMapScene, Starfield } from "./cosmic-map-scene"
import type { StepComponentProps } from "./step-contract"

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

/**
 * Astro Map email capture (Figma LPR_Quiz_Map_Email). Shown right AFTER the
 * scanning storyboard, so its hero is the fully-formed cosmic map ({@link
 * CosmicMapScene} in {@link FINAL_VIEW}) the animation just built — the user
 * gates seeing the rest of it behind their email. The hero grows to fill the
 * free vertical space (map fills it on any device); the form sits below it and
 * the legal note rides the bottom edge (safe-area aware). Owns its CTA and
 * surfaces the design's default → typing → error → submitting states
 * (`step.submitDelayMs` drives the loader before advancing to checkout).
 */
export function AstroMapEmailStep({ step, value, onChange, onAdvance, onBack }: StepComponentProps) {
  const [submitting, setSubmitting] = useState(false)
  const [focused, setFocused] = useState(false)
  const [touched, setTouched] = useState(false)
  const email = typeof value === "string" ? value : ""
  const valid = EMAIL_RE.test(email)
  const active = focused || email.length > 0
  const showError = touched && email.length > 0 && !valid

  const submit = () => {
    if (submitting) return
    if (!valid) {
      setTouched(true)
      return
    }
    if (step.submitDelayMs) {
      setSubmitting(true)
      setTimeout(onAdvance, step.submitDelayMs)
    } else {
      onAdvance()
    }
  }

  return (
    <div className="flex flex-1 flex-col bg-dark-base text-purple-light">
      {/* Hero: the formed cosmic map, grows to fill the free space above the form. */}
      <div className="relative min-h-[160px] flex-1 overflow-hidden">
        <div className="pointer-events-none absolute inset-0">
          <div className="absolute -top-24 left-1/2 size-[420px] -translate-x-1/2 rounded-full bg-main-purple/20 blur-[120px]" />
          <Starfield />
        </div>
        <CosmicMapScene view={FINAL_VIEW} className="absolute inset-0" />
        <button
          type="button"
          onClick={onBack}
          aria-label="Go back"
          className="absolute left-2 top-2 z-10 flex size-10 items-center justify-center rounded-full text-purple-light transition-colors hover:bg-purple-light/10"
        >
          <ArrowLeft className="size-6" />
        </button>
      </div>

      {/* Form: pinned below the hero, legal note on the bottom edge (safe-area aware). */}
      <div className="flex flex-col px-4 pt-5 pb-safe">
        <div className="space-y-2 text-center">
          <h1 className="font-display text-2xl font-medium text-purple-light">{step.title}</h1>
          {step.subtitle && <p className="text-base text-purple-light/70">{step.subtitle}</p>}
        </div>

        <label
          className={cn(
            "mt-6 flex h-12 items-center gap-3 rounded-xl border bg-purple-light/10 px-4 transition-colors",
            showError ? "border-peach" : active ? "border-main-purple" : "border-transparent",
          )}
        >
          <Mail
            className={cn(
              "size-6 shrink-0 transition-colors",
              showError ? "text-peach" : active ? "text-main-purple" : "text-purple-light/40",
            )}
          />
          <input
            type="email"
            inputMode="email"
            autoComplete="email"
            value={email}
            placeholder={step.placeholder}
            onChange={(e) => onChange(e.target.value)}
            onFocus={() => setFocused(true)}
            onBlur={() => {
              setFocused(false)
              setTouched(true)
            }}
            className="h-full flex-1 bg-transparent text-base text-purple-light caret-main-purple outline-none placeholder:text-purple-light/40"
          />
        </label>
        {showError && <p className="mt-2 text-sm text-peach">Please enter a valid email</p>}

        <div className="mt-6">
          {submitting ? (
            <div
              aria-busy="true"
              className="flex h-14 w-full items-center justify-center gap-2 rounded-full bg-purple-light/10 font-body text-base font-semibold text-purple-light/70"
            >
              <Loader2 className="size-6 animate-spin" />
              Continue
            </div>
          ) : (
            <Button variant="gradient" size="xl" onClick={submit}>
              Continue
            </Button>
          )}
        </div>

        {step.legalNote && (
          <div className="mt-4 flex flex-col gap-2 rounded-2xl bg-purple-light/10 p-4">
            <div className="flex items-center gap-3">
              <ShieldCheck className="size-5 shrink-0 text-green" />
              <p className="flex-1 text-xs text-purple-light/70">
                Your personal data is safe with us. We'll use your email for updates, receipts, and
                subscription details.
              </p>
            </div>
            <p className="pl-8 text-xs text-purple-light/70">
              We respect your privacy. By continuing, you agree to our{" "}
              <LegalLink to={AppRoutes.TERMS} className="text-purple-light underline">
                Terms &amp; Conditions
              </LegalLink>{" "}
              and{" "}
              <LegalLink to={AppRoutes.PRIVACY} className="text-purple-light underline">
                Privacy Policy
              </LegalLink>
              .
            </p>
          </div>
        )}
      </div>
    </div>
  )
}
